import axios from 'axios';
import { getItem, logoutUser, setItem, ORG_ACCESS_TOKEN } from './cookieManager';

const baseURL = import.meta.env.VITE_API_BASE_URL;
const REFRESH_ENDPOINT = '/loginService/v1/refreshToken';
const AUTH_BYPASS_PATHS = [
  '/loginService/v1/authenticate',
  '/loginService/v1/authenticateOrganization',
  '/loginService/v1/socialLogin',
  '/loginService/v1/socialLoginOrganization',
  REFRESH_ENDPOINT,
  '/loginService/v1/logout',
];

export const axiosClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

axiosClient.interceptors.request.use((config) => {
  const token = getItem(ORG_ACCESS_TOKEN);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const refreshClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

refreshClient.interceptors.request.use((config) => {
  const token = getItem(ORG_ACCESS_TOKEN);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshRequest = null;

const shouldBypassRefresh = (url = '') =>
  AUTH_BYPASS_PATHS.some((path) => url.includes(path));

const handleAuthFailure = () => {
  logoutUser();
  window.location.href = '/auth/login';
};

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config ?? {};
    const status = error.response?.status;

    // Network / CORS / timeout — do not treat as session expiry
    if (!error.response) {
      return Promise.reject(error);
    }

    if (status === 403) {
      // Don't logout on authorization failures, only authentication failures
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      !originalRequest._retry &&
      !shouldBypassRefresh(originalRequest.url)
    ) {
      originalRequest._retry = true;
      const refreshCall =
        refreshRequest ?? refreshClient.get(REFRESH_ENDPOINT);
      refreshRequest = refreshCall;

      try {
        const refreshResponse = await refreshCall;

        const isSuccessStatus =
          refreshResponse?.status >= 200 && refreshResponse?.status < 300;
        const hasExplicitCode = typeof refreshResponse?.data?.code === 'number';
        const isSuccessCode =
          !hasExplicitCode || refreshResponse?.data?.code === 1;

        const newJwtToken = refreshResponse?.data?.data?.[0]?.jwttoken;
        if (newJwtToken) {
          setItem(ORG_ACCESS_TOKEN, newJwtToken);
        }

        if (isSuccessStatus && isSuccessCode) {
          if (newJwtToken) {
            originalRequest.headers.Authorization = `Bearer ${newJwtToken}`;
          }
          return axiosClient(originalRequest);
        }

        throw new Error('Unable to refresh session');
      } catch (refreshError) {
        handleAuthFailure();
        return Promise.reject(refreshError);
      } finally {
        if (refreshRequest === refreshCall) {
          refreshRequest = null;
        }
      }
    }

    if (status === 401) {
      handleAuthFailure();
    }

    return Promise.reject(error);
  }
);
